import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import GlassModal from './GlassModal';
import { showToast } from './Toast';
import { useUser } from '../../contexts/UserContext';
import { useBrand } from '../../contexts/BrandContext';
import { getDisplayName } from '../../utils/formatters';

/**
 * ReportProblemButton - Floating help button to report issues
 * 
 * Features:
 * - Floating circular button (bottom-right)
 * - Toggle between collapsed (?) and expanded modal
 * - Form with optional screenshot upload
 * - Auto-captures user/device context
 * - Writes to the support_tickets table; the displayed support address is brand-resolved
 * - Rate limited to 3 reports per hour
 */
const ReportProblemButton = () => {
  const { user, isAuthenticated, loading: userLoading } = useUser();
  // Support address follows the company's brand. This used to be a hardcoded
  // info@smbmate.com shown to all 77 TradesMatePro companies as well.
  const { brand } = useBrand();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  // Hide the FAB while any other modal is open — otherwise a second modal
  // stacks on top of an existing one and dismiss buttons get intercepted
  // (see TKT-0012). GlassModal maintains body.dataset.openModals.
  const [otherModalOpen, setOtherModalOpen] = useState(false);
  useEffect(() => {
    const update = () => {
      const count = parseInt(document.body.dataset.openModals || '0', 10);
      // If this component's own modal is open, don't count it.
      setOtherModalOpen(count > (showModal ? 1 : 0));
    };
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.body, { attributes: true, attributeFilter: ['data-open-modals'] });
    return () => obs.disconnect();
  }, [showModal]);
  
  // Form state
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);

  // Rate limiting check
  useEffect(() => {
    const checkRateLimit = () => {
      const now = Date.now();
      const hourMs = 60 * 60 * 1000;
      
      const reportHistory = JSON.parse(localStorage.getItem('report_problem_history') || '[]');
      const recentReports = reportHistory.filter(
        timestamp => now - timestamp < hourMs
      );
      
      setRateLimited(recentReports.length >= 3);
    };

    if (isAuthenticated) {
      checkRateLimit();
    }
  }, [isAuthenticated]);

  // Handle paste for screenshot
  const handleScreenshotPaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          setScreenshot(file);
          // Create preview
          const reader = new FileReader();
          reader.onloadend = () => {
            setScreenshotPreview(reader.result);
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }
  }, []);

  // Auto-capture context
  const getContext = () => {
    const appVersion = process.env.REACT_APP_VERSION || process.env.npm_package_version || 'unknown';
    
    return {
      url: typeof window !== 'undefined' ? window.location.href : '',
      userEmail: user?.email || '',
      userName: getDisplayName(user),
      companyId: user?.company_id || '',
      companyName: user?.company_name || '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      timestamp: new Date().toISOString(),
      appVersion: appVersion
    };
  };

  // Don't render if user is not authenticated or still loading
  if (userLoading || !isAuthenticated) {
    return null;
  }

  // Don't render if rate limited
  if (rateLimited) {
    return null;
  }

  // Don't render the floating button while another modal is open (TKT-0012).
  // If our own modal is open we keep rendering so the user can submit/close it.
  if (otherModalOpen && !showModal) {
    return null;
  }

  const handleButtonClick = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsExpanded(false);
    // Reset form
    setDescription('');
    setSeverity('');
    setStepsToReproduce('');
    setScreenshot(null);
    setScreenshotPreview(null);
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScreenshot(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!description.trim()) {
      showToast('error', 'Please describe what went wrong');
      return;
    }

    setIsSubmitting(true);

    try {
      const context = getContext();

      // Insert directly into support_tickets table
      const { error: insertError } = await supabase
        .from('support_tickets')
        .insert({
          company_id: context.companyId || null,
          user_email: context.userEmail,
          user_name: context.userName,
          description: description.trim(),
          severity: severity || 'Not specified',
          steps_to_reproduce: stepsToReproduce || null,
          page_url: context.url,
          user_agent: context.userAgent,
          app_version: context.appVersion
        });

      if (insertError) {
        throw insertError;
      }

      // Update rate limit history
      const now = Date.now();
      const reportHistory = JSON.parse(localStorage.getItem('report_problem_history') || '[]');
      reportHistory.push(now);
      localStorage.setItem('report_problem_history', JSON.stringify(reportHistory));

      showToast('success', 'Thanks! We\'ll look into this shortly.');
      handleCloseModal();
    } catch (error) {
      console.error('Error submitting bug report:', error);
      showToast('error', 'Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const severityOptions = [
    { value: '', label: 'Select severity (optional)' },
    { value: 'Minor annoyance', label: 'Minor annoyance' },
    { value: 'Something\'s broken', label: 'Something\'s broken' },
    { value: 'Can\'t use the app', label: 'Can\'t use the app' }
  ];

  return (
    <>
      {/* Floating button */}
      <button
        onClick={handleButtonClick}
        className="fixed bottom-6 right-6 z-[9999] w-12 h-12 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2"
        title="Report a problem"
        aria-label="Report a problem"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-6 h-6"
        >
          {/* Chat/feedback bubble — a "?" here read as Help but this opens "Report a problem". */}
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      </button>

      {/* Modal */}
      <GlassModal
        isOpen={showModal}
        onClose={handleCloseModal}
        header={
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-600/20 rounded-full flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 text-primary-400"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9" y1="15" x2="15" y2="15" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Report a Problem</h2>
              <p className="text-sm text-gray-400">Help us improve your experience</p>
            </div>
          </div>
        }
        footer={
          <div className="flex gap-3 justify-end">
            <button
              onClick={handleCloseModal}
              className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !description.trim()}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Sending...
                </span>
              ) : (
                'Send Report'
              )}
            </button>
          </div>
        }
        maxWidth="max-w-lg"
      >
        <form onSubmit={handleSubmit} className="space-y-5" onPaste={handleScreenshotPaste}>
          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              What went wrong? <span className="text-red-400">*</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="No detail is too small — even 'this looks weird' helps us!"
              rows={4}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white/60 dark:bg-white/5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none text-gray-900 dark:text-white placeholder-gray-500"
              required
            />
          </div>

          {/* Severity */}
          <div>
            <label htmlFor="severity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              How serious is this?
            </label>
            <select
              id="severity"
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white/60 dark:bg-white/5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
              style={{ colorScheme: 'dark' }}
            >
              {severityOptions.map((option) => (
                <option key={option.value} value={option.value} style={{ background: '#1a1a2e', color: '#fff' }}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Steps to Reproduce */}
          <div>
            <label htmlFor="steps" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              What were you doing when this happened?
            </label>
            <textarea
              id="steps"
              value={stepsToReproduce}
              onChange={(e) => setStepsToReproduce(e.target.value)}
              placeholder="Step 1: I clicked on... Step 2: Then I saw..."
              rows={3}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white/60 dark:bg-white/5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none text-gray-900 dark:text-white placeholder-gray-500"
            />
          </div>

          {/* Screenshot */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Screenshot (optional)
            </label>
            <div className="relative border-2 border-dashed border-white/20 rounded-lg p-4 text-center hover:border-primary-400 transition-colors">
              {screenshotPreview ? (
                <div className="relative">
                  <img
                    src={screenshotPreview}
                    alt="Screenshot preview"
                    className="max-h-48 mx-auto rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setScreenshot(null);
                      setScreenshotPreview(null);
                    }}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <label htmlFor="screenshot-upload" className="block cursor-pointer text-gray-400">
                  <svg className="w-8 h-8 mx-auto mb-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">Click to upload screenshot</p>
                  <p className="text-xs text-gray-500 mt-1">Or paste from clipboard (Ctrl/Cmd+V)</p>
                </label>
              )}
              <input
                id="screenshot-upload"
                type="file"
                accept="image/*"
                onChange={handleScreenshotChange}
                className="hidden"
              />
            </div>
          </div>

          {/* Privacy note.
              Board #3184: this named an individual's mailbox to every user of every
              company. It was also inaccurate — the form does not email anyone, it inserts
              into support_tickets (see handleSubmit), so "will be sent to <address>"
              described a delivery that does not happen. Saying what actually occurs
              removes the disclosure and the wrong promise at once. */}
          <p className="text-xs text-gray-500 text-center" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Your report goes to the {brand?.name || 'support'} team and includes your account
            info to help us investigate.
          </p>
        </form>
      </GlassModal>
    </>
  );
};

export default ReportProblemButton;
